import Box from '@mui/material/Box';

interface WordmarkProps {
  /** Rendered height, as a CSS length. The width follows the aspect ratio. */
  readonly size?: string | Record<string, string>;
  /**
   * Accessible name. Pass an empty string when the mark sits inside something that
   * already names the brand - a link labelled "Hello Kando - home", for instance - or
   * a screen reader announces the name twice.
   */
  readonly title?: string;
}

/**
 * The Hello Kando wordmark: the full greeting, drawn rather than set.
 *
 * Every letter is a stroked centreline on the same grid as the icon in `Logo.tsx` -
 * one weight throughout, round caps and joins. The K is the icon's K: same stem,
 * same junction at mid-height, same arm angle. The lowercase bowls are ellipses at
 * 0.86 of their height rather than circles, which is what stops a monoline reading
 * as a child's hand.
 *
 * The grid, so a future letter lands on it rather than near it:
 *
 *   baseline y 72.5 · cap top y 27.5 · bowls rx 11.7 ry 13.6 about cy 59.5
 *   letter gap 17 · word space 40 · stroke 8
 *
 * Three details that are easy to lose if this is ever redrawn:
 *
 *  1. Stroke ends are pulled back so the *ink* lands on the baseline, not the path.
 *     A round cap adds half a stroke in every direction, so a vertical stem is
 *     inset by `w/2` while the K's diagonals need `(w/2) / |dy|`, which is larger.
 *     Without it the arms punch through the baseline and the word sits crooked.
 *  2. `Ka` is kerned to 5 against a default of 17. The K's arms recede at
 *     mid-height, so an equal metric gap leaves an optical hole.
 *  3. **The stroke weight is set by the `e`, and nothing else.** A bar-and-bowl `e`
 *     needs white above and below the bar: the counter is `2·ry - w` tall and the
 *     bar eats `w` of it, leaving `ry - w` split between two gaps. At `w = 11` that
 *     is 2.6 units a side - 0.24 of the stroke, about 1.2px at header size - and the
 *     glyph fills in as a solid blob. At `w = 8` it is 5.6 a side (0.70), which
 *     stays open. Every other letter here tolerates a far heavier stroke because it
 *     has at most one counter; do not re-weight the mark from those glyphs alone.
 *
 * Outlined rather than set in a typeface, so the mark cannot reflow while a font
 * loads and cannot drift if the display face changes. It takes `currentColor` and
 * nothing else - hence no hex value here, and no second asset for the dark scheme.
 */
export function Wordmark({ size = '1.75rem', title = 'Hello Kando' }: WordmarkProps) {
  return (
    <Box
      component="svg"
      viewBox="-4 23.5 373.72 53.6"
      role={title ? 'img' : 'presentation'}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={8}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{
        display: 'block',
        height: size,
        width: 'auto',
        aspectRatio: '373.72 / 53.6',
        // The ink fits the viewBox exactly; this only guards against a rounded
        // sub-pixel shaving a cap at small sizes.
        overflow: 'visible',
        color: 'text.primary',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {title ? <title>{title}</title> : null}

      {/* Hello */}
      <path d="M 0,27.5 V 72.5 M 27.32,27.5 V 72.5 M 0,50 H 27.32" />
      {/* e: the bar runs left to right, then the bowl sweeps anticlockwise from the
          right side over the top, down the left and round the bottom, leaving the
          aperture at 5 o'clock. Drawing the bar right-to-left instead mirrors the
          glyph into a reversed e, which is easy to miss until it is set in a word. */}
      <path d="M 44.35,58.5 L 67.69,58.5 A 11.7,13.6 0 1,0 61.87,71.28" />
      <path d="M 84.72,27.5 V 72.5" />
      <path d="M 101.72,27.5 V 72.5" />
      <path d="M 118.72,59.5 a 11.7,13.6 0 1,0 23.4,0 a 11.7,13.6 0 1,0 -23.4,0" />

      {/* Kando, on the same grid, one word space along */}
      <g transform="translate(182.12 0)">
        <path d="M 0,27.5 V 72.5 M 27.32,27.5 L 0,50 L 27.32,72.5" />
        <path d="M 39,59.5 a 11.7,13.6 0 1,0 23.4,0 a 11.7,13.6 0 1,0 -23.4,0 M 62.4,46.5 V 72.5" />
        <path d="M 79.4,72.5 V 59.5 A 11.7,13.6 0 0,1 102.8,59.5 V 72.5" />
        <path d="M 119.8,59.5 a 11.7,13.6 0 1,0 23.4,0 a 11.7,13.6 0 1,0 -23.4,0 M 143.2,27.5 V 72.5" />
        <path d="M 160.2,59.5 a 11.7,13.6 0 1,0 23.4,0 a 11.7,13.6 0 1,0 -23.4,0" />
      </g>
    </Box>
  );
}
